import { useMemo } from "react";
import Popover from "react-bootstrap/Popover";
import { resources } from "@constants/hardcoded-strings";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    useReactTable,
    createColumnHelper,
} from "@tanstack/react-table";
import { useTableFilters } from "@hooks/use-table-filters";
import { SelectOption } from "@idw-types/common";
import { PolicyDecisionConditionTableRowData } from "@idw-types/common/policy-decision-condition-table-row-data";
import { initialTableState } from "@redux/reducers/table-settings-reducer";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "../../pagination-container/pagination-container";
import { IdwTable } from "../../idw-table/idw-table";
import { IdwTableContainer } from "../../idw-table-container/idw-table-container";
import { PolicyDecisionConditionCategoryCell } from "@containers/common/policy-decision-condition-category-cell/policy-decision-condition-category-cell";
import { PolicyDecisionConditionCategoryTypes } from "@enums/policy-decision-condition-category-types";
import styles from "./policy-decision-conditions-table-container.less";

type PolicyDecisionConditionsTableContainerProps = {
    policyDecisionConditionTableRows: Array<PolicyDecisionConditionTableRowData>;
    onClickTableRow: (condition: PolicyDecisionConditionTableRowData) => void;
};

export const PolicyDecisionConditionsTableContainer = (props: PolicyDecisionConditionsTableContainerProps) => {
    const { policyDecisionConditionTableRows, onClickTableRow } = props;

    const typeColumnFilterOptions = useMemo(() => {
        const options: Array<SelectOption> = [];

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.type === "acquiredAttribute")) {
            options.push({ label: "acquired attribute", value: "acquiredAttribute" });
        }

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.attributeField)) {
            options.push({ label: "attribute", value: "attribute" });
        }

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.component)) {
            options.push({ label: "component", value: "component" });
        }

        return options;
    }, [policyDecisionConditionTableRows]);

    const categoryColumnFilterOptions = useMemo(() => {
        const options: Array<SelectOption> = [];

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.RISK)) {
            options.push({ label: "Risk", value: PolicyDecisionConditionCategoryTypes.RISK });
        }

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.CONTROL)) {
            options.push({ label: "Control", value: PolicyDecisionConditionCategoryTypes.CONTROL });
        }

        if (policyDecisionConditionTableRows.some(tableRow => tableRow.category === PolicyDecisionConditionCategoryTypes.VERIFICATION)) {
            options.push({ label: "Verification", value: PolicyDecisionConditionCategoryTypes.VERIFICATION });
        }

        return options;
    }, [policyDecisionConditionTableRows]);

    const columnHelper = createColumnHelper<PolicyDecisionConditionTableRowData>();

    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.deployedPolicyComponents.columns.name.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("category", {
            header: resources.tables.deployedPolicyComponents.columns.class.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <PolicyDecisionConditionCategoryCell category={props.row.original.category} />,
            meta: {
                filterOptions: categoryColumnFilterOptions
            }
        }),
        columnHelper.accessor("type", {
            header: resources.tables.deployedPolicyComponents.columns.type.header,
            filterFn: "singleSelectFilter",
            cell: (props) => <div className={styles.policyDecisionConditionTypeRow}>{props.row.original.type}</div>,
            meta: {
                filterOptions: typeColumnFilterOptions
            }
        })
    ]), [policyDecisionConditionTableRows]);

    const { filterTypes } = useTableFilters();

    const useTableResult: any = useReactTable({
        columns,
        data: policyDecisionConditionTableRows,
        autoResetPageIndex: true,
        filterFns: filterTypes,
        sortDescFirst: false,
        initialState: {
            ...initialTableState,
            pagination: {
                ...initialTableState.pagination,
                pageSize: 10
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    const renderTableRowHoverComponent = (props: any, row: { original: PolicyDecisionConditionTableRowData }) => {
        const { original } = row;
        let prefix = "";
        if (original.attributeField) {
            prefix = "Attribute: ";
        }
        return (
            <Popover className={styles.tableRowPopover} id={"policy-decision-condition-table-row-popover"} {...props} >
                <Popover.Header>{prefix}{original.name}</Popover.Header>
                <Popover.Body>
                    {original.description}
                </Popover.Body>
            </Popover>
        );
    };

    const handleTableRowClick = (row: { original: PolicyDecisionConditionTableRowData }) => {
        const { original } = row;
        onClickTableRow(original);
    };

    return (
        <IdwTableContainer className={styles.policyDecisionConditionsTableContainer}>
            <IdwTable
                id="policy-decision-condition"
                className={styles.policyDecisionConditionsTable}
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"policy-decision-condition"}
                onClickTableRow={handleTableRowClick}
                renderTableRowHoverComponent={renderTableRowHoverComponent}
            />
            <PaginationContainer
                idPrefix="policy-decision-condition"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};